import { Fragment, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Search } from "lucide-react";
import NotificationCenter from "@/components/ui/NotificationCenter";
import ViewAsDropdown from "@/components/ui/ViewAsDropdown";
import UserMenu from "@/components/ui/UserMenu";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useScope } from "@/lib/scope/scope-context";
import { useRBAC } from "@/lib/rbac/hooks";
import { SUPER_ADMIN_ROLE } from "@/lib/rbac/types";
import { findNavMatch, useVisibleNav } from "@/lib/rbac/use-nav";

const APP_TITLE = "TudoNum Admin";

const IS_MAC =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);

/**
 * Priority ranking for role → header-badge selection.
 *
 * Higher wins when a user holds multiple roles. Unknown / custom roles
 * fall through to a default weight (10) so they still show up as the
 * badge if the user has nothing else. The numbers are stable: a new
 * role can be inserted without reshuffling the existing ones.
 */
const ROLE_RANK: Record<string, number> = {
  super_admin: 100,
  platform_admin: 90,
  compliance_admin: 80,
  operations_admin: 80,
  finance_admin: 80,
  support_admin: 70,
  department_manager: 60,
  ticket_queue_lead: 50,
  ticket_queue_member: 40,
  city_manager: 35,
  zone_supervisor: 30,
};

interface Crumb {
  label: string;
  to?: string;
}

export default function Header({
  toggleSidebar,
  onLogout,
  onOpenSearch,
}: {
  toggleSidebar: () => void;
  onLogout: () => void;
  onOpenSearch: () => void;
}) {
  const { scope, isFranchiseOwner } = useScope();
  const { roles } = useRBAC();
  const { pathname } = useLocation();
  const sections = useVisibleNav();

  // Pick the highest-ranked active role so the header reflects the
  // most authoritative identity the user holds. If a lead is ALSO a
  // support_admin (unlikely but possible), the header says
  // "Support Admin" because that role has broader authority than
  // "Ticket Queue Lead".
  const topRole = useMemo(() => {
    const active = roles.filter((r) => r.is_active);
    if (active.length === 0) return null;
    return [...active].sort((a, b) => {
      const ra = ROLE_RANK[a.role_name] ?? 10;
      const rb = ROLE_RANK[b.role_name] ?? 10;
      return rb - ra;
    })[0];
  }, [roles]);

  const isIPOwner = topRole?.role_name === SUPER_ADMIN_ROLE;

  // Three header identities:
  //   1. Franchise user -> franchise tier + entity name (existing).
  //   2. IP Owner (super_admin) -> "Global Operations Command · IP Owner".
  //   3. Everyone else internal -> "Internal Team · <their top role>".
  //
  // Someone with no active role shouldn't reach this component at all
  // (the login gate blocks them), but we fall back to "Workspace" as a
  // soft default just in case.
  let headerLabel: string;
  let headerBadge: string;

  if (isFranchiseOwner && scope) {
    headerLabel = scope.franchise_tier_display ?? "";
    headerBadge = scope.franchise_entity_name ?? "";
  } else if (isIPOwner) {
    headerLabel = "Global Operations Command";
    headerBadge = "IP Owner";
  } else if (topRole) {
    headerLabel = "Internal Team";
    headerBadge = topRole.role_display_name;
  } else {
    headerLabel = "Workspace";
    headerBadge = "";
  }

  // ─── Breadcrumbs + document title from the nav config ────────────────────
  const match = useMemo(() => findNavMatch(pathname, sections), [pathname, sections]);

  const crumbs = useMemo<Crumb[]>(() => {
    if (!match) return [];
    const { section, group, item, isExact } = match;
    const pageLabel = group.isSingle ? group.label : item.label;
    const trail: Crumb[] = [];
    if (section.label) trail.push({ label: section.label });
    if (!group.isSingle) trail.push({ label: group.label });
    trail.push({ label: pageLabel, to: isExact ? undefined : item.path });
    if (!isExact) trail.push({ label: "Details" });
    return trail;
  }, [match]);

  useEffect(() => {
    if (!match) {
      document.title = APP_TITLE;
      return;
    }
    const { group, item, isExact } = match;
    const parts = [
      !isExact && "Details",
      group.isSingle ? group.label : item.label,
      !group.isSingle && group.label,
      APP_TITLE,
    ].filter(Boolean);
    document.title = parts.join(" · ");
  }, [match]);

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-card/95 px-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] backdrop-blur supports-[backdrop-filter]:bg-card/80 md:px-6">
      {/* Hamburger (mobile) */}
      <button
        type="button"
        className="md:hidden -ml-1 p-1.5 rounded-lg hover:bg-muted transition-colors"
        onClick={toggleSidebar}
        aria-label="Open navigation"
      >
        <Menu className="w-5 h-5 text-foreground" />
      </button>

      {/* Breadcrumbs (full trail on sm+, current page only on phones) */}
      <div className="min-w-0 flex-1">
        {crumbs.length > 0 && (
          <Breadcrumb>
            <BreadcrumbList className="flex-nowrap text-[13px]">
              {crumbs.map((crumb, i) => {
                const isLast = i === crumbs.length - 1;
                return (
                  <Fragment key={`${crumb.label}-${i}`}>
                    <BreadcrumbItem className={isLast ? "min-w-0" : "hidden sm:inline-flex"}>
                      {isLast ? (
                        <BreadcrumbPage className="truncate font-medium">
                          {crumb.label}
                        </BreadcrumbPage>
                      ) : crumb.to ? (
                        <BreadcrumbLink asChild>
                          <Link to={crumb.to}>{crumb.label}</Link>
                        </BreadcrumbLink>
                      ) : (
                        <span className="whitespace-nowrap">{crumb.label}</span>
                      )}
                    </BreadcrumbItem>
                    {!isLast && <BreadcrumbSeparator className="hidden sm:inline-flex" />}
                  </Fragment>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
        )}
      </div>

      {/* Right cluster */}
      <div className="flex shrink-0 items-center gap-2">
        {/* Search / command palette */}
        <button
          type="button"
          onClick={onOpenSearch}
          aria-label="Search pages"
          className="flex h-9 items-center gap-2 rounded-lg border border-border bg-background px-2.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:w-60"
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className="hidden lg:inline">Search pages…</span>
          <kbd className="ml-auto hidden rounded border border-border bg-muted px-1.5 text-[10px] font-medium lg:inline">
            {IS_MAC ? "⌘K" : "Ctrl K"}
          </kbd>
        </button>

        {/* Identity chip */}
        {headerBadge && (
          <span
            className="hidden xl:inline-flex max-w-[220px] items-center truncate rounded bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary"
            title={headerLabel ? `${headerLabel} · ${headerBadge}` : headerBadge}
          >
            {headerBadge}
          </span>
        )}

        <ViewAsDropdown />
        <NotificationCenter />
        <UserMenu
          identityLabel={headerLabel}
          identityBadge={headerBadge}
          onLogout={onLogout}
        />
      </div>
    </header>
  );
}
